/* __next_internal_client_entry_do_not_use__ LanguageDropdown auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getWindow } from "@faire/web--source/common/globals/getWindow";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { languageOptionsWithDisambiguatedLocale } from "@faire/web--source/common/localization/languageOptions";
import { languageWithCountryOptions } from "@faire/web--source/common/localization/languageWithCountryOptions";
import { setLocale, setLocaleForCookie } from "@faire/web--source/common/localization/setLocale";
import { appendFaireDirectBrandAliasQueryParam } from "@faire/web--source/common/routes/faire-direct/appendFaireDirectBrandAliasQueryParam";
import { isRobot } from "@faire/web--source/common/user-agent/isRobot";
import { Button } from "@faire/web--source/slate/Button";
import { ButtonStyles } from "@faire/web--source/slate/Button/styles";
import { Spacer } from "@faire/web--source/slate/spacing";
import { useCurrentPath } from "@faire/web--source/ui/hooks/useCurrentPath";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { localizePath, localizePathForRobot, removeLocaleFromUrl } from "@faire/web--source/ui/routing/util";
import { trackInternationalLanguageSelectorLanguageClickClick } from "@faire/web-api--source/events/international/click/languageSelectorLanguageClick";
import { Language } from "@faire/web-api--source/indigofair/languages/Language";
import React, { useContext } from "react";
import styled from "styled-components";
import { RoutingContext } from "../routing/RoutingContext";
/**
 * @trackcomponent
 * @supersededBy "@faire/web--source/slate/Dropdown/Dropdown#Dropdown"
 */ export var LanguageDropdown = function(param) {
    var availableLocales = param.availableLocales, localeOverrideKey = param.localeOverrideKey, eventName = param.eventName, eventKey = param.eventKey, userToken = param.userToken, assignLocalizedUrl = param.assignLocalizedUrl;
    var _useContext = useContext(RoutingContext), faireDirectBrandAlias = _useContext.faireDirectBrandAlias, faireDirectBrandToken = _useContext.faireDirectBrandToken;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var currentPath = useCurrentPath();
    var languages = languageOptionsWithDisambiguatedLocale(availableLocales);
    var getUrlFromLanguageAndLocaleKey = function(language, localeKey) {
        if (!language) {
            return "";
        }
        var url = !assignLocalizedUrl ? removeLocaleFromUrl(currentPath.toString()) : localizePath(currentPath.toString(), language, localeKey);
        if (faireDirectBrandAlias && faireDirectBrandToken) {
            url = appendFaireDirectBrandAliasQueryParam(url, faireDirectBrandAlias, faireDirectBrandToken);
        }
        return url;
    };
    // TODO(kelvin.chu): once the MOVE_REFERRER_KEY_UP feature is stable,
    // the assignLocalizedUrl and useLinks props should be removed.
    // we should use ListLinks as the default while still updating the cookie
    // this may require some rework to the onClick function in ListLink
    var assignUrl = function(language, localeKey) {
        var _getWindow;
        var url = getUrlFromLanguageAndLocaleKey(language, localeKey);
        (_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : _getWindow.location.assign(url);
    };
    var handleLanguageClick = function(option) {
        return function() {
            if (option.value.language === Language.LANGUAGE_UNKNOWN) {
                return;
            }
            if (option.value.language === undefined) {
                return;
            }
            var localeKey = option.value;
            var lang = option.value.language;
            if (localeOverrideKey) {
                setLocaleForCookie(lang, localeOverrideKey, localeKey);
                assignUrl(lang, localeKey);
            } else {
                setLocale(lang, userToken, localeKey).then(function() {
                    assignUrl(lang, localeKey);
                });
            }
            if (eventKey) {
                trackInternationalLanguageSelectorLanguageClickClick(eventName, eventKey, option.value.language);
            }
        };
    };
    if (isRobot()) {
        var languageWithCountry = languageWithCountryOptions(availableLocales, strictLocalize);
        return languageWithCountry.map(function(option) {
            return /*#__PURE__*/ _jsxs(React.Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(ListLink, {
                        to: localizePathForRobot(currentPath.toString(), option.value),
                        skipPrependLocale: true,
                        children: option.label
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "2x"
                    })
                ]
            }, option.label);
        });
    }
    return languages.filter(function(option) {
        return option.value.language !== undefined;
    }).map(function(languageOption) {
        return /*#__PURE__*/ _jsxs(React.Fragment, {
            children: [
                /*#__PURE__*/ _jsx(ListItem, {
                    onClick: handleLanguageClick(languageOption),
                    children: languageOption.label
                }),
                /*#__PURE__*/ _jsx(Spacer, {
                    height: "2x"
                })
            ]
        }, languageOption.label);
    });
};
var ListItem = styled(Button).attrs({
    variant: "tertiary"
}).withConfig({
    componentId: "sc-648f1c53-0"
})([
    "text-decoration:none;&:hover{text-decoration:underline;}"
]);
var ListLink = styled(Link).withConfig({
    componentId: "sc-648f1c53-1"
})([
    "",
    " text-decoration:none;&:hover{text-decoration:underline;}"
], function(props) {
    return ButtonStyles({
        $variant: "tertiary",
        theme: props.theme
    });
});
